// Booking Calendar — visual availability grid. Rooms are rows, days are columns.
// Reservations render as colored blocks that span their check-in→check-out range.
// Clicking a block navigates to its detail page.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, ChevronLeft, ChevronRight, Ban, Plus } from "lucide-react";
import { adminApi } from "@/lib/adminAuth";

const DAYS = 30;

const STATUS_COLOR = {
    pending: "bg-amber-200 text-amber-900 border-amber-300",
    confirmed: "bg-jade/25 text-jade border-jade/40",
    checked_in: "bg-navy text-ivory border-navy",
    checked_out: "bg-navy/30 text-navy border-navy/40",
    cancelled: "bg-red-100 text-red-700 border-red-200 line-through",
    no_show: "bg-neutral-200 text-neutral-600 border-neutral-300",
};

function addDays(iso, n) {
    const d = new Date(iso + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
}
function daysBetween(a, b) {
    return Math.round((new Date(b + "T00:00:00Z") - new Date(a + "T00:00:00Z")) / 86400000);
}
function fmtHead(iso) {
    const d = new Date(iso + "T00:00:00Z");
    return { m: d.toLocaleDateString("en-US", { month: "short", timeZone: "UTC" }), n: d.getUTCDate(), w: d.toLocaleDateString("en-US", { weekday: "narrow", timeZone: "UTC" }) };
}

export default function AdminCalendar() {
    const [start, setStart] = useState(() => new Date().toISOString().slice(0, 10));
    const [data, setData] = useState({ rooms: [], reservations: [], blocks: [] });
    const [loading, setLoading] = useState(true);
    const [showBlock, setShowBlock] = useState(false);

    const end = useMemo(() => addDays(start, DAYS), [start]);
    const dates = useMemo(() => Array.from({ length: DAYS }, (_, i) => addDays(start, i)), [start]);

    const load = async () => {
        setLoading(true);
        try {
            const [cal, blk] = await Promise.all([
                adminApi.get(`/admin/calendar?start=${start}&end=${end}`),
                adminApi.get("/admin/blocked-dates"),
            ]);
            setData({ rooms: cal.data.rooms || [], reservations: cal.data.reservations || [], blocks: blk.data || [] });
        } catch (e) {
            toast.error("Could not load calendar");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [start]);

    return (
        <div data-testid="admin-calendar-page">
            <header className="mb-6 flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.28em] text-gold">Availability</div>
                    <h1 className="font-display text-4xl md:text-5xl text-[#0b1f22] mt-3">Booking calendar</h1>
                    <p className="text-[#0b1f22]/60 mt-2 text-sm">Reservations and blocked dates across the next {DAYS} days. Click a block to open the reservation.</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => setStart(addDays(start, -14))} className="p-2 rounded-full border border-navy/20 hover:bg-navy hover:text-ivory" data-testid="cal-prev"><ChevronLeft size={14} /></button>
                    <button onClick={() => setStart(new Date().toISOString().slice(0, 10))} className="text-[11px] uppercase tracking-[0.16em] px-4 py-2 rounded-full border border-navy/20 hover:bg-navy hover:text-ivory" data-testid="cal-today">Today</button>
                    <button onClick={() => setStart(addDays(start, 14))} className="p-2 rounded-full border border-navy/20 hover:bg-navy hover:text-ivory" data-testid="cal-next"><ChevronRight size={14} /></button>
                    <button onClick={() => setShowBlock(true)} className="ml-2 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] px-4 py-2 rounded-full bg-navy text-ivory" data-testid="cal-block">
                        <Ban size={12} /> Block dates
                    </button>
                </div>
            </header>

            <div className="mb-4 flex items-center gap-3 flex-wrap text-[10px] uppercase tracking-[0.14em]">
                <span className="px-2.5 py-1 rounded-full bg-amber-200 text-amber-900">Pending</span>
                <span className="px-2.5 py-1 rounded-full bg-jade/25 text-jade">Confirmed</span>
                <span className="px-2.5 py-1 rounded-full bg-navy text-ivory">Checked in</span>
                <span className="px-2.5 py-1 rounded-full bg-navy/30 text-navy">Checked out</span>
                <span className="px-2.5 py-1 rounded-full bg-red-100 text-red-700 line-through">Cancelled</span>
                <span className="px-2.5 py-1 rounded-full bg-neutral-800 text-white">Blocked</span>
            </div>

            {loading ? <div className="text-navy/60 text-sm">Loading calendar…</div> : (
                <div className="bg-white rounded-2xl border border-navy/10 overflow-x-auto">
                    <div className="min-w-[1200px]">
                        {/* Header row */}
                        <div className="grid sticky top-0 bg-white z-10" style={{ gridTemplateColumns: `220px repeat(${DAYS}, minmax(34px, 1fr))` }}>
                            <div className="px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-navy/50 border-b border-r border-navy/10">Room</div>
                            {dates.map((d) => {
                                const h = fmtHead(d);
                                const isWknd = ["S", "s"].includes(h.w);
                                return (
                                    <div key={d} className={`text-center py-2 border-b border-navy/10 text-[10px] ${isWknd ? "bg-navy/5" : ""}`}>
                                        <div className="text-navy/50 uppercase tracking-[0.14em]">{h.w}</div>
                                        <div className="text-navy font-medium mt-0.5">{h.n}</div>
                                        <div className="text-navy/40">{h.m}</div>
                                    </div>
                                );
                            })}
                        </div>

                        {/* Room rows */}
                        {data.rooms.map((room) => {
                            const resThisRoom = data.reservations.filter((r) => r.room_slug === room.slug);
                            const blkThisRoom = data.blocks.filter((b) => b.room_slug === room.slug);
                            return (
                                <div key={room.slug} className="relative grid border-b border-navy/10 hover:bg-navy/[.02]" style={{ gridTemplateColumns: `220px repeat(${DAYS}, minmax(34px, 1fr))`, minHeight: 56 }}>
                                    <div className="px-4 py-3 text-sm text-navy border-r border-navy/10 flex flex-col justify-center">
                                        <div className="font-medium truncate">{room.name}</div>
                                        {room.published === false && <div className="text-[10px] uppercase tracking-[0.14em] text-navy/40 mt-0.5">Unpublished</div>}
                                    </div>
                                    {/* Empty cells (kept for the grid) */}
                                    {dates.map((d) => <div key={d} className="h-full" />)}
                                    {/* Overlay reservation blocks */}
                                    {resThisRoom.map((r) => {
                                        const s = r.check_in > start ? r.check_in : start;
                                        const e = r.check_out < end ? r.check_out : end;
                                        const startIdx = daysBetween(start, s);
                                        const span = Math.max(1, daysBetween(s, e));
                                        if (startIdx >= DAYS || startIdx + span <= 0) return null;
                                        const col = 2 + startIdx; // account for room label col
                                        return (
                                            <Link
                                                key={r.id}
                                                to={`/admin/reservations/${r.id}`}
                                                data-testid={`cal-res-${r.id}`}
                                                title={`${r.guest_name} · ${r.check_in} → ${r.check_out}`}
                                                className={`absolute top-2 bottom-2 px-2 py-1 text-[11px] rounded-md border shadow-sm truncate ${STATUS_COLOR[r.status] || "bg-navy/10 border-navy/20"}`}
                                                style={{
                                                    left: `calc(220px + ${(startIdx / DAYS) * 100}% * ${DAYS} / ${DAYS} - 0px)`,
                                                    // Grid pos via percentage of remaining width
                                                    width: `calc((100% - 220px) * ${span} / ${DAYS} - 4px)`,
                                                }}
                                            >
                                                {r.guest_name || "Reservation"}
                                            </Link>
                                        );
                                    })}
                                    {/* Overlay blocked-date bars */}
                                    {blkThisRoom.map((b) => {
                                        const s = b.start_date > start ? b.start_date : start;
                                        const e = b.end_date < end ? b.end_date : end;
                                        const startIdx = daysBetween(start, s);
                                        const span = Math.max(1, daysBetween(s, e));
                                        if (startIdx >= DAYS || startIdx + span <= 0) return null;
                                        return (
                                            <div
                                                key={b.id}
                                                data-testid={`cal-block-${b.id}`}
                                                title={`${b.reason} · ${b.start_date} → ${b.end_date}${b.note ? " · " + b.note : ""}`}
                                                className="absolute top-2 bottom-2 px-2 py-1 text-[11px] rounded-md bg-neutral-800 text-white opacity-90 truncate"
                                                style={{
                                                    left: `calc(220px + (100% - 220px) * ${startIdx} / ${DAYS})`,
                                                    width: `calc((100% - 220px) * ${span} / ${DAYS} - 4px)`,
                                                }}
                                            >
                                                <Ban size={11} className="inline mr-1" /> {b.reason.replace("_", " ")}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {showBlock && <BlockModal rooms={data.rooms} onClose={() => setShowBlock(false)} onSaved={() => { setShowBlock(false); load(); }} />}
        </div>
    );
}

function BlockModal({ rooms, onClose, onSaved }) {
    const [form, setForm] = useState({
        room_slug: rooms[0]?.slug || "",
        start_date: new Date().toISOString().slice(0, 10),
        end_date: addDays(new Date().toISOString().slice(0, 10), 1),
        reason: "maintenance",
        note: "",
    });
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        setBusy(true);
        try {
            await adminApi.post("/admin/blocked-dates", form);
            toast.success("Dates blocked");
            onSaved();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not block dates");
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="block-modal">
            <div className="bg-white rounded-2xl w-full max-w-md p-6">
                <div className="text-[10px] uppercase tracking-[0.22em] text-gold">Block dates</div>
                <h2 className="font-display text-2xl text-navy mt-1 mb-4">Take a room offline</h2>
                <div className="space-y-3">
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Room</span>
                        <select data-testid="block-room" value={form.room_slug} onChange={(e) => setForm({ ...form, room_slug: e.target.value })} className="mt-1.5 w-full border border-navy/15 rounded-lg px-3 py-2 text-sm">
                            {rooms.map((r) => <option key={r.slug} value={r.slug}>{r.name}</option>)}
                        </select>
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Start</span>
                            <input data-testid="block-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5 w-full border border-navy/15 rounded-lg px-3 py-2 text-sm" />
                        </label>
                        <label className="block">
                            <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">End</span>
                            <input data-testid="block-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1.5 w-full border border-navy/15 rounded-lg px-3 py-2 text-sm" />
                        </label>
                    </div>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Reason</span>
                        <select data-testid="block-reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-1.5 w-full border border-navy/15 rounded-lg px-3 py-2 text-sm">
                            <option value="maintenance">Maintenance</option>
                            <option value="out_of_service">Out of service</option>
                            <option value="private_event">Private event</option>
                            <option value="owner_use">Owner use</option>
                            <option value="other">Other</option>
                        </select>
                    </label>
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Internal note</span>
                        <textarea data-testid="block-note" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5 w-full border border-navy/15 rounded-lg px-3 py-2 text-sm" />
                    </label>
                </div>
                <div className="mt-5 flex items-center justify-end gap-3">
                    <button onClick={onClose} className="px-5 py-2.5 text-sm text-navy hover:bg-navy/5 rounded-full">Cancel</button>
                    <button onClick={submit} disabled={busy} data-testid="block-save" className="px-6 py-2.5 rounded-full bg-navy text-ivory text-[11px] uppercase tracking-[0.16em] inline-flex items-center gap-2">
                        <Plus size={13} /> {busy ? "Saving…" : "Block dates"}
                    </button>
                </div>
            </div>
        </div>
    );
}
